import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { TopicWizard } from '@/components/topics/topic-wizard'
import { reportQuery, subaccountEntitlementsQuery, tagsQuery, topicsQuery } from '@/lib/queries'
import { rtaQuery, useSetRtaTopics } from '@/lib/rta-queries'
import { emptyTopicDraft, useTopicStore } from '@/lib/topic-store'

/**
 * `rtaId` means the wizard was opened from an RTA's Topics tab (CLI-744): "Used for" starts on
 * real-time alerts, the topic is attached to that RTA as it saves (which records the use anyway),
 * and the wizard returns to the RTA.
 */
const newSearchSchema = z.object({
  rtaId: z.string().optional(),
  /** Opened from a report's Headers tab ("New topic"): "Used for" starts on reports, and
   *  save/cancel land back on that report's Headers tab, where the new topic leads Available. */
  returnReportId: z.string().optional(),
})

export const Route = createFileRoute('/app/accounts/$accountId_/topics/$subaccountId/new')({
  validateSearch: newSearchSchema,
  // Prefetch what the wizard reads: the tag pool (shared workbench), the topic list (the
  // duplicate-live check), and the account's entitlements (which data sources are unlocked).
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(tagsQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(topicsQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(
        subaccountEntitlementsQuery(params.accountId, params.subaccountId),
      ),
    ]),
  component: NewTopic,
})

function NewTopic() {
  const { accountId, subaccountId } = Route.useParams()
  const { rtaId, returnReportId } = Route.useSearch()
  const returnReport = useQuery({
    ...reportQuery(returnReportId ?? ''),
    enabled: !!returnReportId && !rtaId,
  })
  const queryClient = useQueryClient()
  const { addTopic } = useTopicStore(subaccountId)
  const rta = useQuery({ ...rtaQuery(rtaId ?? ''), enabled: !!rtaId })
  const attach = useSetRtaTopics(rtaId ?? '', queryClient)

  if (!rtaId) {
    return (
      <TopicWizard
        accountId={accountId}
        subaccountId={subaccountId}
        initialDraft={
          returnReportId ? { ...emptyTopicDraft(), products: ['reports'] } : emptyTopicDraft()
        }
        heading="New topic"
        submitLabel="Create topic"
        {...(returnReportId && {
          returnTo: {
            accountId,
            reportId: returnReportId,
            ...(returnReport.data?.name ? { reportName: returnReport.data.name } : {}),
          },
        })}
        onSubmit={(draft) => addTopic(draft)}
      />
    )
  }
  return (
    <TopicWizard
      accountId={accountId}
      subaccountId={subaccountId}
      initialDraft={{ ...emptyTopicDraft(), products: ['rta'] }}
      heading="New topic"
      submitLabel="Create topic"
      returnToRta={{
        accountId,
        rtaId,
        ...(rta.data?.name ? { rtaName: rta.data.name } : {}),
      }}
      onSubmit={async (draft) => {
        const created = await addTopic(draft)
        // Attach it where it was made for, keeping whatever the RTA already listens on.
        const current = rta.data?.topics ?? []
        await attach.mutateAsync([...current, { topicId: created.id, dataSources: [] }])
      }}
    />
  )
}
